import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { normalizeMediaObject } from 'api';
import { useGlobalContext } from 'globalContext';
import { useAnalytics } from 'hooks';
import { MediaPlayer, useWrapperContext, menuItemTrigger } from 'msp-components';
import ButtonShare from 'components/ButtonShare';
import Error404 from 'components/Error404';

const StyledContainer = styled.div`
  background-color: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const MediaTitle = ({ routeProps, displayOptions, ...rest }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const media = normalizeMediaObject(rest);

  const { renderControls, toggleMenu } = useWrapperContext();
  const controls = renderControls(null);
  const portalEl = controls && controls.containerInfo;

  const { activeMenuItem, toggleShareSheet } = useGlobalContext();

  const { onEnd, imageLayout } = displayOptions;
  const menuItemTriggerTimeoutRef = useRef();
  const handleMediaEnd = () => {
    if (onEnd === 'share') {
      toggleShareSheet();
    } else if (onEnd.indexOf('menu__') === 0) {
      const menuItemId = onEnd.replace('menu__', '');
      toggleMenu(true);
      menuItemTriggerTimeoutRef.current = setTimeout(() => menuItemTrigger(menuItemId), 500);
    } else {
      // if onEnd === 'poster' or is undefined
      playerRef.current.handleRequestSeek(0);
    }
  };

  useEffect(() => {
    return () => clearTimeout(menuItemTriggerTimeoutRef.current);
  }, []);

  // pause player when menu item is opened
  const playerRef = useRef();
  useEffect(() => {
    if (activeMenuItem && playerRef.current) {
      playerRef.current.handleRequestPause();
    }
  }, [activeMenuItem]);

  // analytics event tracking
  const { trackMediaPlay, trackMediaProgress } = useAnalytics();

  if (!routeProps.match.isExact) return <Error404 />;

  return (
    <StyledContainer key={media.source}>
      {controls}
      {controls && (
        <MediaPlayer
          {...media}
          posterFrameDisplayStyle={imageLayout}
          afterControls={media.type === 'video' && !isFullscreen && <ButtonShare />}
          allowFullscreen={media.type === 'video'}
          controlsPortalEl={isFullscreen ? null : portalEl}
          onPlay={trackMediaPlay}
          onProgress={trackMediaProgress}
          onEnd={handleMediaEnd}
          onFullscreenChange={setIsFullscreen}
          ref={playerRef}
        />
      )}
    </StyledContainer>
  );
};

MediaTitle.propTypes = {
  routeProps: PropTypes.object,
  type: PropTypes.oneOf(['video', 'audio']),
  source: PropTypes.string,
  versions: PropTypes.arrayOf(PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    url: PropTypes.string,
  })),
  metadata: PropTypes.shape({
    sourceId: PropTypes.string,
    sourceUrl: PropTypes.string,
  }),
  displayOptions: PropTypes.shape({
    onEnd: PropTypes.oneOf(['poster', 'share']),
  }),
};

export default MediaTitle;
